import { useState } from 'react';
import type {
  BookOverride,
  ClientBookMode,
  Location,
  Role,
  Staff,
  StaffEngagement,
} from '@salonomy/types';
import { Button, Field, Modal, Select, TextInput } from '@salonomy/ui';
import { useCreateStaff, useUpdateStaff } from '../../data/staff.js';
import { useAuth } from '../../lib/auth.js';
import { ownsSealedBook } from '../../lib/book-mode.js';
import { ALL_ROLES, assignRoleReason, canAssignRole, ROLE_LABEL } from '../../lib/rbac.js';
import { useToast } from '../../lib/toast.js';

const ENGAGEMENTS: Array<{ value: StaffEngagement; label: string }> = [
  { value: 'employed', label: 'Employed' },
  { value: 'chair_renter', label: 'Chair renter' },
];

const OVERRIDES: Array<{ value: BookOverride; label: string }> = [
  { value: 'inherit', label: 'Inherit salon default' },
  { value: 'shared', label: 'Shared (visible salon-wide)' },
  { value: 'sealed', label: 'Sealed (theirs alone)' },
];

/**
 * Create / edit a team member (WS6). Role-escalation guards mirror the API exactly: a role at or
 * above the actor's own rank is rendered as a DISABLED option with an inline explanation, never a
 * server 403. Only an owner may edit an owner record — when that's not allowed the caller doesn't
 * open this modal at all (the Edit control is hidden), but we re-check defensively.
 */
export function StaffModal({
  existing,
  locations,
  clientBookMode,
  onClose,
}: {
  existing?: Staff;
  locations: Location[];
  clientBookMode: ClientBookMode;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const create = useCreateStaff();
  const update = useUpdateStaff();
  const toast = useToast();
  const editing = Boolean(existing);
  const actorRole: Role = user?.role ?? 'receptionist';

  const [displayName, setDisplayName] = useState(existing?.displayName ?? '');
  const [role, setRole] = useState<Role>(existing?.role ?? 'stylist');
  const [engagement, setEngagement] = useState<StaffEngagement>(existing?.engagement ?? 'employed');
  const [bookOverride, setBookOverride] = useState<BookOverride>(
    existing?.bookOverride ?? 'inherit',
  );
  const [locationId, setLocationId] = useState<string>(existing?.locationId ?? '');
  const [email, setEmail] = useState(existing?.email ?? '');
  const [phone, setPhone] = useState(existing?.phone ?? '');
  const [title, setTitle] = useState(existing?.title ?? '');
  const [busy, setBusy] = useState(false);

  // The book override only has meaning in a `mixed` salon; in shared/sealed it follows the mode.
  const showOverride = clientBookMode === 'mixed';
  // Effective sealing for this person — must mirror the server's source of truth exactly
  // (apps/api/src/modules/staff/book-mode.ts ownsSealedBook), or the sealed-book consequence is
  // silently invisible where staff are created/edited. Note: a SEALED salon seals an EMPLOYED
  // person's book too, and a 'sealed' override seals regardless of engagement.
  const effectivelySealed = ownsSealedBook(clientBookMode, engagement, bookOverride);

  const roleDisabledReason = assignRoleReason(actorRole, role);

  async function submit() {
    if (!displayName.trim()) {
      toast.error('A display name is required.');
      return;
    }
    if (!canAssignRole(actorRole, role)) {
      // Defensive: the Select disables these, but a stale selection could slip through.
      toast.error(roleDisabledReason ?? 'You cannot assign that role.');
      return;
    }
    setBusy(true);
    const body = {
      displayName: displayName.trim(),
      role,
      engagement,
      bookOverride,
      locationId: locationId || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      title: title.trim() || null,
    };
    try {
      if (editing && existing) {
        await update.mutateAsync({ id: existing.id, body });
        toast.success('Team member updated');
      } else {
        await create.mutateAsync(body);
        toast.success('Team member added');
      }
      onClose();
    } catch {
      toast.error('Could not save the team member.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit team member' : 'Add team member'}
      width={520}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {editing ? 'Save' : 'Add member'}
          </Button>
        </>
      }
    >
      <Field label="Display name">
        <TextInput
          autoFocus
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="e.g. Amara K."
        />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field
          label="Role"
          error={roleDisabledReason ?? undefined}
          hint={roleDisabledReason ? undefined : 'Governs what they can manage.'}
        >
          <Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ALL_ROLES.map((r) => {
              const blocked = !canAssignRole(actorRole, r);
              return (
                <option key={r} value={r} disabled={blocked}>
                  {ROLE_LABEL[r]}
                  {blocked ? ' — not permitted' : ''}
                </option>
              );
            })}
          </Select>
        </Field>
        <Field label="Engagement" hint="Chair renters can own a sealed book.">
          <Select
            value={engagement}
            onChange={(e) => setEngagement(e.target.value as StaffEngagement)}
          >
            {ENGAGEMENTS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {showOverride ? (
        <Field
          label="Client book"
          hint="This salon is in mixed mode — choose how this person's book behaves."
        >
          <Select
            value={bookOverride}
            onChange={(e) => setBookOverride(e.target.value as BookOverride)}
          >
            {OVERRIDES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}

      {effectivelySealed ? (
        <div
          className="muted"
          style={{
            fontSize: 12.5,
            background: 'var(--slm-porcelain)',
            border: '1px solid var(--slm-border)',
            borderRadius: 8,
            padding: '8px 10px',
            margin: '2px 0 6px',
          }}
        >
          This person's clients are sealed — only they can see them, isolated from the rest of the
          salon.
        </div>
      ) : null}

      <Field label="Location" hint={locations.length ? undefined : 'No locations yet.'}>
        <Select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
          <option value="">No specific location</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </Select>
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Email" hint="Used for their console invite.">
          <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Mobile">
          <TextInput
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+44 7700 900123"
          />
        </Field>
      </div>

      <Field label="Title" hint='Free text, e.g. "Colour specialist".'>
        <TextInput value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
    </Modal>
  );
}
